import React, { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

// Icons
const ClipboardIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
)

const ClockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const ExclamationIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
)

const UserGroupIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)

// Complaint Card Component
const ComplaintCard = ({ complaint, onAssign, onUpdateStatus, onEscalate, workers }) => {
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'assigned': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-orange-100 text-orange-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{complaint.title}</h3>
          <p className="text-sm text-gray-500">#{complaint.complaint_number}</p>
        </div>
        <div className="flex space-x-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
            {complaint.status.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(complaint.priority)}`}>
            {complaint.priority.toUpperCase()}
          </span>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4">{complaint.description}</p>
      
      <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
        <span>By: {complaint.first_name} {complaint.last_name}</span>
        <span>{new Date(complaint.created_at).toLocaleDateString()}</span>
      </div>

      {complaint.assigned_to && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Assigned to:</strong> {complaint.assigned_worker_name || 'Worker'}
          </p>
          {complaint.estimated_resolution_date && (
            <p className="text-sm text-blue-600">
              <strong>Due:</strong> {new Date(complaint.estimated_resolution_date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
      
      <div className="flex space-x-2">
        {complaint.status === 'pending' && (
          <Button
            onClick={() => onAssign(complaint.id)}
            variant="primary"
            size="sm"
          >
            Assign
          </Button>
        )}
        
        {complaint.status === 'assigned' && (
          <Button
            onClick={() => onUpdateStatus(complaint.id, 'in_progress')}
            variant="outline"
            size="sm"
          >
            Start Work
          </Button>
        )}
        
        {complaint.status === 'in_progress' && (
          <Button
            onClick={() => onUpdateStatus(complaint.id, 'resolved')}
            variant="outline"
            size="sm"
          >
            Mark Resolved
          </Button>
        )}
        
        <Button
          onClick={() => onUpdateStatus(complaint.id, 'on_hold')}
          variant="outline"
          size="sm"
        >
          Hold
        </Button>
        
        <Button
          onClick={() => onEscalate(complaint.id)}
          variant="outline"
          size="sm"
          className="text-red-600 border-red-300 hover:bg-red-50"
        >
          Escalate
        </Button>
      </div>
    </div>
  )
}

// Escalation Modal Component
const EscalationModal = ({ isOpen, onClose, onEscalate, complaintId, admins }) => {
  const [escalatedTo, setEscalatedTo] = useState('')
  const [reason, setReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      await onEscalate(complaintId, escalatedTo, reason)
      setEscalatedTo('')
      setReason('')
      onClose()
    } catch (error) {
      console.error('Error escalating complaint:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Escalate Complaint</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Escalate To *
            </label>
            <select
              required
              value={escalatedTo}
              onChange={(e) => setEscalatedTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose an admin</option>
              {admins.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.first_name} {admin.last_name} ({admin.role})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Escalation Reason *
            </label>
            <textarea
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Explain why this complaint needs to be escalated..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Escalating...' : 'Escalate Complaint'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Assignment Modal Component
const AssignmentModal = ({ isOpen, onClose, onAssign, complaintId, workers }) => {
  const [selectedWorker, setSelectedWorker] = useState('')
  const [estimatedDate, setEstimatedDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      await onAssign(complaintId, selectedWorker, estimatedDate)
      onClose()
    } catch (error) {
      console.error('Error assigning complaint:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Assign Complaint</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Worker *
            </label>
            <select
              required
              value={selectedWorker}
              onChange={(e) => setSelectedWorker(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a worker</option>
              {workers.map((worker) => (
                <option key={worker.id} value={worker.id}>
                  {worker.first_name} {worker.last_name} ({worker.workload} tasks)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Resolution Date
            </label>
            <input
              type="datetime-local"
              value={estimatedDate}
              onChange={(e) => setEstimatedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              {isLoading ? 'Assigning...' : 'Assign Complaint'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Main Department Head Dashboard Component
const DepartmentHeadDashboard = () => {
  const { user } = useAuthStore()
  const [complaints, setComplaints] = useState([])
  const [workers, setWorkers] = useState([])
  const [admins, setAdmins] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [showEscalationModal, setShowEscalationModal] = useState(false)
  const [selectedComplaintId, setSelectedComplaintId] = useState(null)

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      
      setIsLoading(true)
      try {
        // Get the department head's assigned departments (if available)
        let departmentIds: string[] = [];
        try {
          const deptResponse = await fetch('/api/v1/dashboard/my-departments', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (deptResponse.ok) {
            const deptData = await deptResponse.json();
            departmentIds = (deptData.data || []).map((d: any) => d.id);
          }
        } catch (error) {
          console.warn('Could not fetch departments, backend will filter automatically', error);
        }

        // Fetch complaints - backend automatically filters by department head's departments
        // Use a high limit to get all complaints for the department head
        const complaintsResponse = await fetch('/api/v1/complaints?limit=1000', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (complaintsResponse.ok) {
          const complaintsData = await complaintsResponse.json()
          const fetchedComplaints = complaintsData.data || []
          setComplaints(fetchedComplaints)

          // Fetch workers in department head's departments
          // If we have department IDs, filter workers by those departments
          let workersResponse;
          if (departmentIds.length > 0) {
            // Fetch all front workers and filter by department
            workersResponse = await fetch('/api/v1/users?role=front_worker', {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            })
          } else {
            // Fallback: fetch all front workers (backend may filter)
            workersResponse = await fetch('/api/v1/users?role=front_worker', {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            })
          }
          
          if (workersResponse.ok) {
            const workersData = await workersResponse.json()
            let allWorkers = workersData.data || []
            
            // Filter workers by department head's departments if we have department IDs
            if (departmentIds.length > 0) {
              // We need to check each worker's departments
              // For now, we'll show all workers and let the backend handle filtering
              // In a real scenario, you'd need an endpoint to get workers by department
              allWorkers = allWorkers.filter((worker: any) => {
                // If worker has department_id, check if it matches
                if (worker.department_id && departmentIds.includes(worker.department_id)) {
                  return true;
                }
                // Otherwise include all (backend will handle proper filtering)
                return true;
              });
            }
            
            // Add workload count for each worker
            const workersWithWorkload = allWorkers.map((worker: any) => ({
              ...worker,
              workload: fetchedComplaints.filter((c: any) => c.assigned_to === worker.id && c.status !== 'resolved').length
            }))
            setWorkers(workersWithWorkload)
          }

          // Fetch admins for escalation
          const adminsResponse = await fetch('/api/v1/users?role=universal_admin,office_admin', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (adminsResponse.ok) {
            const adminsData = await adminsResponse.json()
            setAdmins(adminsData.data || [])
          }
        } else {
          console.error('Error fetching complaints:', complaintsResponse.statusText)
          setComplaints([])
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        setComplaints([])
        setWorkers([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [user?.id])

  const handleAssign = async (complaintId, workerId, estimatedDate) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assigned_to: workerId,
          estimated_resolution_date: estimatedDate
        })
      })

      if (response.ok) {
        const result = await response.json()
        // Refresh data to get updated complaints
        const fetchData = async () => {
          const complaintsResponse = await fetch('/api/v1/complaints?limit=1000', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
          if (complaintsResponse.ok) {
            const complaintsData = await complaintsResponse.json()
            setComplaints(complaintsData.data || [])
          }
        }
        await fetchData()
        alert('Complaint assigned successfully!')
      } else {
        const error = await response.json()
        alert('Error assigning complaint: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error assigning complaint:', error)
      alert('Error assigning complaint. Please try again.')
    }
  }

  const handleUpdateStatus = async (complaintId, newStatus) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        const result = await response.json()
        // Refresh data to get updated complaints
        const fetchData = async () => {
          const complaintsResponse = await fetch('/api/v1/complaints?limit=1000', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
          if (complaintsResponse.ok) {
            const complaintsData = await complaintsResponse.json()
            setComplaints(complaintsData.data || [])
          }
        }
        await fetchData()
        alert('Status updated successfully!')
      } else {
        const error = await response.json()
        alert('Error updating status: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Error updating status. Please try again.')
    }
  }

  const handleEscalate = async (complaintId, escalatedTo, reason) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}/escalate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          escalated_to: escalatedTo,
          reason: reason
        })
      })

      if (response.ok) {
        const result = await response.json()
        // Refresh data to get updated complaints
        const fetchData = async () => {
          const complaintsResponse = await fetch('/api/v1/complaints?limit=1000', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
          if (complaintsResponse.ok) {
            const complaintsData = await complaintsResponse.json()
            setComplaints(complaintsData.data || [])
          }
        }
        await fetchData()
        alert('Complaint escalated successfully!')
      } else {
        const error = await response.json()
        alert('Error escalating complaint: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error escalating complaint:', error)
      alert('Error escalating complaint. Please try again.')
    }
  }

  const openAssignmentModal = (complaintId) => {
    setSelectedComplaintId(complaintId)
    setShowAssignmentModal(true)
  }

  const openEscalationModal = (complaintId) => {
    setSelectedComplaintId(complaintId)
    setShowEscalationModal(true)
  }

  const filteredComplaints = complaints.filter(complaint => {
    if (activeTab === 'all') return true
    return complaint.status === activeTab
  })

  const stats = [
    {
      title: 'Total Complaints',
      value: complaints.length,
      icon: ClipboardIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Pending Assignment',
      value: complaints.filter(c => c.status === 'pending').length,
      icon: ClockIcon,
      color: 'bg-yellow-500'
    },
    {
      title: 'In Progress',
      value: complaints.filter(c => c.status === 'in_progress').length,
      icon: ExclamationIcon,
      color: 'bg-orange-500'
    },
    {
      title: 'Resolved',
      value: complaints.filter(c => c.status === 'resolved').length,
      icon: CheckCircleIcon,
      color: 'bg-green-500'
    }
  ]

  const tabs = [
    { id: 'all', label: 'All Complaints', count: complaints.length },
    { id: 'pending', label: 'Pending', count: complaints.filter(c => c.status === 'pending').length },
    { id: 'assigned', label: 'Assigned', count: complaints.filter(c => c.status === 'assigned').length },
    { id: 'in_progress', label: 'In Progress', count: complaints.filter(c => c.status === 'in_progress').length },
    { id: 'resolved', label: 'Resolved', count: complaints.filter(c => c.status === 'resolved').length }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Department Head Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Welcome back, {user?.first_name}! Manage your department's complaints.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Workers Overview */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <UserGroupIcon className="w-5 h-5 mr-2" />
              Department Workers
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workers.map((worker) => (
                <div key={worker.id} className="p-4 border rounded-lg">
                  <h3 className="font-medium">{worker.first_name} {worker.last_name}</h3>
                  <p className="text-sm text-gray-500">Active Tasks: {worker.workload}</p>
                  <p className="text-sm text-gray-500">{worker.email}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Complaints Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Complaints Management</h2>
          </div>
          
          {/* Tabs */}
          <div className="px-6 py-4 border-b border-gray-200">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          {/* Complaints List */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredComplaints.map((complaint) => (
                  <ComplaintCard
                    key={complaint.id}
                    complaint={complaint}
                    onAssign={openAssignmentModal}
                    onUpdateStatus={handleUpdateStatus}
                    onEscalate={openEscalationModal}
                    workers={workers}
                  />
                ))}
              </div>
            )}
            
            {!isLoading && filteredComplaints.length === 0 && (
              <div className="text-center py-8">
                <ClipboardIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No complaints found for this category.</p>
              </div>
            )}
          </div>
        </div>

        {/* Assignment Modal */}
        <AssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
          complaintId={selectedComplaintId}
          workers={workers}
        />

        {/* Escalation Modal */}
        <EscalationModal
          isOpen={showEscalationModal}
          onClose={() => setShowEscalationModal(false)}
          onEscalate={handleEscalate}
          complaintId={selectedComplaintId}
          admins={admins}
        />
      </div>
    </div>
  )
}

export default DepartmentHeadDashboard
